import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Zap, Phone, Lock, ArrowLeft, KeyRound, Wrench, Gauge, ShieldCheck, ArrowRight, Clock, Sparkles, MapPin, Mail } from "lucide-react";
import MacJitLogo from "../components/MacJitLogo";
import Marquee from "react-fast-marquee";
import { toast } from "sonner";
import { roleHomePath } from "../lib/roles";
import { moduleHome } from "../lib/subscriptionModules";

const SERVICES = [
  { name: "Oil & Filter Change", price: 400, duration: "30m", desc: "Engine oil flush · oil & air filter · fluid top-up" },
  { name: "General Service", price: 1500, duration: "2h", desc: "30-point check · tyre pressure · adjustments · cleaning" },
  { name: "Battery Replacement", price: 1200, duration: "45m", desc: "Battery health check · replacement · charging system test" },
  { name: "Wheel Truing & Balancing", price: 600, duration: "45m", desc: "Spoke tension · rim truing · dynamic wheel balance" },
  { name: "Brake Service", price: 900, duration: "1h", desc: "Brake shoe / disc pad · fluid bleed · cable adjustment" },
  { name: "Full Service", price: 3000, duration: "3h 30m", desc: "Engine, brakes, electrics, tyres, wash & polish" },
  { name: "Engine Repair", price: 4500, duration: "4h", desc: "Diagnostics · valve clearance · part replacement" },
];

const STEPS = [
  { n: "01", t: "Walk in / Phone", d: "Drop your car at reception. We capture only your name + phone." },
  { n: "02", t: "Auto-Allocated", d: "Mechanic & bay assigned automatically. ETA sent to you instantly." },
  { n: "03", t: "Watch Live", d: "Open the link on your phone. See your car being serviced live." },
  { n: "04", t: "Approve & Pay", d: "Approve heavy work in one tap. Pay via UPI or cash. Done." },
];

const WORKSHOP_VISUALS = {
  fallback: "https://images.unsplash.com/photo-1487754180451-c456f719a1fc?auto=format&fit=crop&w=1400&q=85",
  gallery: [
    {
      src: "https://images.unsplash.com/photo-1487754180451-c456f719a1fc?auto=format&fit=crop&w=1200&q=85",
      label: "Service bay",
      title: "Car diagnostics",
    },
    {
      src: "https://images.unsplash.com/photo-1625047509248-ec889cbff17f?auto=format&fit=crop&w=1200&q=85",
      label: "Car care",
      title: "Hands-on repair",
    },
    {
      src: "https://images.unsplash.com/photo-1503376780353-7e6692767b70?auto=format&fit=crop&w=1200&q=85",
      label: "Final QA",
      title: "Road-test ready",
    },
  ],
};

const timezoneCity = () => {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
  const part = timezone.split("/").pop() || "Location";
  return part.replace(/_/g, " ");
};

const detectCity = async (lat, lon) => {
  const url = `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lon}&zoom=10&addressdetails=1`;
  const response = await fetch(url, { headers: { Accept: "application/json" } });
  if (!response.ok) throw new Error("reverse geocode failed");
  const data = await response.json();
  const address = data.address || {};
  return address.city || address.town || address.village || address.suburb || address.county || address.state || timezoneCity();
};

/**
 * Terminal login only: staff sign in with mobile + password.
 * Platform admins can sign in with email + password from the same terminal.
 * If a crew account still has the initial password set by the admin,
 * we force a one-time password reset before letting them into the dashboard.
 */
export default function LoginPage() {
  const { login, changePassword } = useAuth();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const staffLogin = searchParams.get("staff") || "";

  const [identifier, setIdentifier] = useState(staffLogin);
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [city, setCity] = useState(timezoneCity());

  // force-reset modal state
  const [forceReset, setForceReset] = useState(null); // { user }
  const [newPwd, setNewPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");

  const formatPhone = (value) => {
    const trimmed = value.trim();
    if (!trimmed) return "";
    if (trimmed.startsWith("+")) return trimmed.replace(/[^\d+]/g, "");
    const digits = trimmed.replace(/\D/g, "");
    if (!digits) return "";
    if (digits.startsWith("91") && digits.length > 10) return `+${digits.slice(0, 12)}`;
    return `+91${digits.slice(0, 10)}`;
  };

  const formatIdentifier = (value) => {
    const trimmed = value.trim();
    if (trimmed.includes("@") || /[a-zA-Z]/.test(trimmed)) return trimmed.toLowerCase();
    return formatPhone(trimmed);
  };

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (!("geolocation" in navigator)) return undefined;
    navigator.geolocation.getCurrentPosition(
      async ({ coords }) => {
        try {
          const detected = await detectCity(coords.latitude, coords.longitude);
          if (!cancelled && detected) setCity(detected.split(",")[0].trim());
        } catch {}
      },
      () => {},
      { enableHighAccuracy: false, timeout: 7000, maximumAge: 30 * 60 * 1000 }
    );
    return () => { cancelled = true; };
  }, []);

  const submit = async (e) => {
    e?.preventDefault();
    const loginIdentifier = identifier.trim();
    if (!loginIdentifier || !password) return toast.error("Enter your mobile/email and password");
    setBusy(true);
    try {
      const { user, mustReset } = await login(loginIdentifier, password);
      if (mustReset) {
        setForceReset({ user });
        toast.message("Set a new password to continue");
      } else {
        toast.success(`Welcome, ${user.name}`);
        nav(moduleHome(user) || roleHomePath(user.role));
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally { setBusy(false); }
  };

  const submitNewPassword = async (e) => {
    e?.preventDefault();
    if (newPwd.length < 8) return toast.error("New password must be at least 8 characters");
    if (newPwd !== confirmPwd) return toast.error("Passwords do not match");
    setBusy(true);
    try {
      await changePassword(password, newPwd);
      toast.success("Password updated");
      const u = forceReset?.user;
      setForceReset(null);
      if (u) nav(moduleHome(u) || roleHomePath(u.role));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update password");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-black grid-bg text-white overflow-hidden">
      <div className="bg-orange-500 text-black border-b-2 border-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-1.5">
          <Marquee speed={50} gradient={false}>
            <span className="font-mono text-[11px] uppercase tracking-[0.3em] mx-8 font-bold">TERMINAL OPS / TENANT BAYS / STAFF ACCESS / CUSTOMER TRACKING / PARTS MARKETPLACE /</span>
            <span className="font-mono text-[11px] uppercase tracking-[0.3em] mx-8 font-bold">TERMINAL OPS / TENANT BAYS / STAFF ACCESS / CUSTOMER TRACKING / PARTS MARKETPLACE /</span>
          </Marquee>
        </div>
      </div>
      <div className="min-h-screen max-w-7xl mx-auto grid lg:grid-cols-12">
        <section className="lg:col-span-7 relative min-h-[46vh] lg:min-h-screen flex flex-col justify-between p-6 sm:p-10">
          <img
            src="https://images.unsplash.com/photo-1493238792000-8113da705763?auto=format&fit=crop&w=1400&q=85"
            className="absolute inset-0 w-full h-full object-cover opacity-35"
            alt=""
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/75 to-black/20" />
          <div className="relative">
            <button onClick={() => nav("/")} data-testid="back-home" className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-zinc-400 hover:text-orange-500 mb-10">
              <ArrowLeft className="w-3 h-3" /> Home
            </button>
            <div className="flex items-center gap-3 mb-10">
              <MacJitLogo size={44} />
              <div className="leading-none">
                <span className="font-display font-black text-2xl tracking-tighter text-white block"><span>MAC</span><span className="text-orange-500">JIT</span></span>
                <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-orange-500/90">Mechanic Just In Time</span>
              </div>
            </div>
            <p className="font-mono text-[10px] uppercase tracking-[0.35em] text-orange-500 mb-4">Workshop Control Room</p>
            <h1 className="font-display font-black text-6xl sm:text-7xl xl:text-8xl tracking-tighter leading-[0.88] uppercase">
              Terminal<br /><span className="text-orange-500">Ignition.</span>
            </h1>
            <p className="text-zinc-300 mt-7 max-w-xl font-mono text-sm leading-relaxed">
              Sign in to assign bays, move jobs, approve bills, manage parts, and keep every car moving through the floor.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <button data-testid="terminal-track" onClick={() => nav("/track")} className="interactive-lift bg-orange-500 hover:bg-orange-400 text-black font-display font-black uppercase tracking-[0.16em] text-xs sm:text-sm px-6 sm:px-7 py-3.5 sm:py-4 transition-colors flex items-center gap-2 border-b-4 border-orange-700 active:translate-y-1 active:border-b-0 shadow-[0_14px_40px_rgba(249,115,22,0.26)]">
                Track Car <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
          <div className="relative grid sm:grid-cols-3 gap-px bg-white/10">
            {[
              { icon: Gauge, label: "Live bays", value: "Synced" },
              { icon: Wrench, label: "Jobs", value: "Queued" },
              { icon: ShieldCheck, label: "Access", value: "Secured" },
            ].map(({ icon: Icon, label, value }) => (
              <div key={label} className="bg-black/70 backdrop-blur p-4">
                <Icon className="w-4 h-4 text-orange-500 mb-3" />
                <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-500">{label}</p>
                <p className="font-display font-black uppercase tracking-tight">{value}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="lg:col-span-5 flex items-center p-6 sm:p-10 bg-zinc-950">
          <div className="w-full max-w-md mx-auto">
            <div className="border border-zinc-800 bg-black p-6 sm:p-8 shadow-2xl shadow-orange-500/5">
              <div className="flex items-center justify-between gap-4 mb-8">
                <div>
                  <div className="flex flex-wrap items-center gap-3 mb-2">
                    <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-orange-500">Terminal sign in</p>
                    <span className="inline-flex items-center gap-1.5 border border-orange-500/30 bg-orange-500/5 px-2 py-1 font-mono text-[9px] uppercase tracking-widest text-orange-500">
                      <MapPin className="w-3 h-3" /> {city}
                    </span>
                  </div>
                  <h2 className="font-display font-black text-3xl text-white tracking-tight uppercase">Crew Access</h2>
                </div>
                <div className="w-12 h-12 bg-orange-500 text-black grid place-items-center">
                  <KeyRound className="w-6 h-6" />
                </div>
              </div>

              <form onSubmit={submit} className="space-y-4">
                <div>
                  <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Registered mobile / platform email</label>
                  <div className="relative mt-2">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                    <input
                      data-testid="login-phone"
                      type="text"
                      placeholder="+91... or platform email"
                      value={identifier}
                      onChange={(e) => setIdentifier(formatIdentifier(e.target.value))}
                      required
                      inputMode="text"
                      autoComplete="username"
                      spellCheck={false}
                      className="w-full bg-zinc-950 border border-zinc-800 pl-10 pr-4 py-3 font-mono text-white focus:border-orange-500 focus:outline-none"
                    />
                  </div>
                  <div className="mt-2 flex flex-wrap gap-2 font-mono text-[9px] uppercase tracking-[0.16em] text-zinc-500">
                    <span className="border border-zinc-800 px-2 py-1">Mobile login</span>
                    <span className="border border-zinc-800 px-2 py-1">Platform email</span>
                  </div>
                  {staffLogin && (
                    <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.16em] text-zinc-500">
                      Use the temporary password from your email, then set a new password.
                    </p>
                  )}
                </div>
                <div>
                  <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Password</label>
                  <div className="relative mt-2">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                    <input data-testid="login-password" type="password" placeholder="Workshop password" value={password} onChange={(e) => setPassword(e.target.value)} required className="w-full bg-zinc-950 border border-zinc-800 pl-10 pr-4 py-3 font-mono text-white focus:border-orange-500 focus:outline-none" />
                  </div>
                </div>
                <button data-testid="login-submit" disabled={busy} className="w-full bg-orange-500 hover:bg-orange-400 text-black font-display font-black uppercase tracking-widest py-4 transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                  {busy ? "Starting console..." : <>Open Terminal <ArrowRight className="w-4 h-4" /></>}
                </button>
                <button type="button" onClick={() => { setForgotEmail(identifier.includes("@") ? identifier : ""); setForgotOpen(true); }} className="w-full border border-zinc-800 py-3 font-display text-xs font-black uppercase tracking-widest text-zinc-300 hover:border-orange-500 hover:text-orange-500">
                  Forgot password
                </button>
              </form>

              <div className="mt-8 pt-6 border-t border-zinc-800">
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                  Customers securely track cars at
                  <button onClick={() => nav("/track")} className="text-orange-500 hover:text-orange-400 ml-1">
                    /track
                  </button>.
                </p>
              </div>
            </div>
            <div className="mt-4 flex items-center gap-2 text-zinc-600 font-mono text-[10px] uppercase tracking-[0.2em] flex-wrap">
              <span className="flex items-center gap-2"><Zap className="w-3 h-3 text-orange-500" />macjit.com</span><span>/</span>
              <span>hello@macjit.com</span><span>/</span><span>+91 93534 01156</span>
            </div>
          </div>
        </section>
      </div>

      <section id="services" className="max-w-7xl mx-auto px-6 py-20 reveal-up reveal-delay-2">
        <div className="grid lg:grid-cols-12 gap-8 mb-12">
          <div className="lg:col-span-5">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-orange-500 mb-2">01 — Services</p>
            <h2 className="font-display font-black text-5xl lg:text-6xl tracking-tighter uppercase">Pick what your<br />car needs.</h2>
          </div>
          <p className="lg:col-span-6 lg:col-start-7 text-zinc-400 font-mono text-sm self-end">
            Transparent base pricing. Parts billed at MRP. No surprise add-ons. SILVER & GOLD members get up to 10% off the entire bill, automatically.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-zinc-900">
          {SERVICES.map((s, i) => (
            <div key={s.name} data-testid={`terminal-service-${s.name.toLowerCase().replace(/ /g, '-')}`} className="bg-zinc-950 p-7 hover:bg-orange-500/5 group transition-colors hover-soft">
              <div className="flex items-start justify-between mb-6">
                <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">/{(i + 1).toString().padStart(2, "0")}</span>
                <span className="bg-zinc-900 text-orange-500 font-mono text-[10px] uppercase tracking-widest px-2 py-1"><Clock className="w-3 h-3 inline mr-1" />{s.duration}</span>
              </div>
              <h3 className="font-display font-black text-2xl tracking-tight mb-2 group-hover:text-orange-500 transition-colors">{s.name}</h3>
              <p className="text-zinc-500 text-sm mb-6">{s.desc}</p>
              <p className="font-display font-black text-4xl tracking-tighter">₹{s.price}<span className="text-zinc-600 text-sm font-mono ml-2">starting</span></p>
            </div>
          ))}
          <div className="bg-gradient-to-br from-orange-500 to-orange-400 text-black p-7 flex flex-col justify-between">
            <div>
              <Sparkles className="w-7 h-7 mb-4" />
              <h3 className="font-display font-black text-2xl tracking-tight mb-2">Loyalty rewards</h3>
              <p className="text-sm mb-2">Spend ₹10k → SILVER (5% off)</p>
              <p className="text-sm">Spend ₹25k → GOLD (10% off)</p>
            </div>
          </div>
        </div>
      </section>

      <section id="how" className="bg-zinc-900/40 border-y border-zinc-900 reveal-up">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-orange-500 mb-2">02 — Process</p>
          <h2 className="font-display font-black text-5xl lg:text-6xl tracking-tighter uppercase mb-16">From keys in<br />to keys back.</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-zinc-800">
            {STEPS.map((s) => (
              <div key={s.n} className="bg-zinc-950 p-8 relative overflow-hidden hover-soft">
                <span className="font-display font-black text-[120px] leading-none tracking-tighter text-orange-500/10 absolute -top-4 -right-2 select-none">{s.n}</span>
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-orange-500 relative">Step {s.n}</p>
                <h3 className="font-display font-black text-2xl tracking-tight mt-3 relative">{s.t}</h3>
                <p className="text-zinc-400 text-sm mt-2 relative">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-20 reveal-up reveal-delay-2">
        <div className="grid lg:grid-cols-12 gap-8 mb-8">
          <div className="lg:col-span-5">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-orange-500 mb-2">03 — Workshop moments</p>
            <h2 className="font-display font-black text-4xl lg:text-5xl tracking-tighter uppercase">Real garage.<br />Real care.</h2>
          </div>
          <p className="lg:col-span-6 lg:col-start-7 text-zinc-400 font-mono text-sm self-end">
            From diagnostics to final road test, every car is handled with a consistent quality checklist and mechanic-level attention.
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          {WORKSHOP_VISUALS.gallery.map((img, idx) => (
            <div key={img.src} className="relative overflow-hidden border border-zinc-800 bg-zinc-900 group rounded-sm">
              <img
                src={img.src}
                alt={`MacJit workshop scene ${idx + 1}`}
                className="h-64 w-full object-cover group-hover:scale-105 transition-transform duration-500"
                onError={(e) => { e.currentTarget.src = WORKSHOP_VISUALS.fallback; }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/15 to-transparent" />
              <div className="absolute top-3 left-3 bg-black/75 backdrop-blur border border-orange-500/30 px-2.5 py-1.5 flex items-center gap-2">
                <MacJitLogo size={20} />
                <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-orange-500">{img.label}</span>
              </div>
              <p className="absolute bottom-4 left-4 right-4 font-display font-black text-2xl tracking-tight">{img.title}</p>
            </div>
          ))}
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-4 sm:gap-6 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-600">
          <span className="flex items-center gap-2"><ShieldCheck className="w-3 h-3 text-orange-500" />Trained technicians only</span>
          <span className="flex items-center gap-2"><Wrench className="w-3 h-3 text-orange-500" />Genuine & OEM parts</span>
          <span className="flex items-center gap-2"><Zap className="w-3 h-3 text-orange-500" />Road-tested before delivery</span>
        </div>
      </section>

      {/* Force-reset modal */}
      {forceReset && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur grid place-items-center p-4" data-testid="force-reset-modal">
          <form onSubmit={submitNewPassword}
            className="w-full max-w-md bg-zinc-950 border border-orange-500/50 p-8 space-y-4">
            <div className="flex items-center gap-3">
              <KeyRound className="w-6 h-6 text-orange-500" />
              <h3 className="font-display font-black text-2xl text-white tracking-tight uppercase">Set a new password</h3>
            </div>
            <p className="font-mono text-xs text-zinc-400">
              You're using the temporary password your admin set. Choose a new password to continue.
            </p>
            <input
              data-testid="new-password"
              type="password" placeholder="New password (min 8 chars)" value={newPwd}
              onChange={(e) => setNewPwd(e.target.value)} required minLength={8}
              className="w-full bg-zinc-900 border border-zinc-800 px-4 py-3 font-mono text-white focus:border-orange-500 focus:outline-none"
            />
            <input
              data-testid="confirm-password"
              type="password" placeholder="Confirm new password" value={confirmPwd}
              onChange={(e) => setConfirmPwd(e.target.value)} required minLength={8}
              className="w-full bg-zinc-900 border border-zinc-800 px-4 py-3 font-mono text-white focus:border-orange-500 focus:outline-none"
            />
            <button disabled={busy} data-testid="submit-new-password"
              className="w-full bg-orange-500 hover:bg-orange-400 text-black font-display font-black uppercase tracking-widest py-3 transition-colors disabled:opacity-50">
              {busy ? "Saving..." : "Update Password →"}
            </button>
          </form>
        </div>
      )}

      {forgotOpen && (
        <ForgotPasswordModal
          email={forgotEmail}
          setEmail={setForgotEmail}
          busy={busy}
          setBusy={setBusy}
          onClose={() => setForgotOpen(false)}
        />
      )}
    </div>
  );
}

function ForgotPasswordModal({ email, setEmail, busy, setBusy, onClose }) {
  const { requestPasswordReset } = useAuth();
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await requestPasswordReset(email.trim().toLowerCase());
      toast.success("If the email exists, reset link has been sent");
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send reset link");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur grid place-items-center p-4">
      <form onSubmit={submit} className="w-full max-w-md border border-orange-500/40 bg-zinc-950 p-8 shadow-2xl shadow-orange-500/10">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-orange-500">Account recovery</p>
            <h3 className="mt-2 font-display text-3xl font-black uppercase tracking-tight text-white">Reset password</h3>
          </div>
          <button type="button" onClick={onClose} className="border border-zinc-800 px-3 py-2 font-mono text-xs text-zinc-400 hover:border-orange-500 hover:text-orange-500">X</button>
        </div>
        <p className="mt-4 text-sm leading-relaxed text-zinc-400">Enter the registered email. We will send a secure reset link to that mailbox.</p>
        <label className="mt-6 block">
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Registered email</span>
          <div className="relative mt-2">
            <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value.toLowerCase())} className="w-full border border-zinc-800 bg-zinc-900 py-3 pl-10 pr-4 font-mono text-white outline-none focus:border-orange-500" placeholder="owner@example.com" />
          </div>
        </label>
        <button disabled={busy} className="mt-6 w-full bg-orange-500 py-3 font-display font-black uppercase tracking-widest text-black hover:bg-orange-400 disabled:opacity-50">
          {busy ? "Sending..." : "Send reset link"}
        </button>
      </form>
    </div>
  );
}
